/**
 * @file 公共 `checkWin` 的实现（M1-T3）。
 *
 * 作为 `win.ts`（牌型分解判定）与 `scoring.ts`（番型计算）之间的**适配层**：
 * 把契约层的 {@link WinCheckInput}（手牌 id 列表）转换为两者共用的 27 维计数向量，
 * 调用纯判定 / 计分，再封顶回 {@link WinCheckResult}。
 *
 * 平台无关、纯函数，不引用任何 DOM / Node / Workers 全局。
 */

import type { TileId } from './types';
import { kindIndexFromTileId } from './tiles';
import { canWinCounts, hasQueTileCounts } from './win';
import { computeFan, deriveContext } from './scoring';
import { M1_ENFORCED_FAN_CAP } from './config';
import type { WinCheckInput, WinCheckResult } from './index';

/** 由手牌（不含 winningTile）+ 将胡的那张牌，构造 27 维计数向量。 */
function buildCounts(hand: readonly TileId[], winningTile: TileId): Uint8Array {
  const counts = new Uint8Array(27);
  for (const id of hand) {
    const i = kindIndexFromTileId(id);
    counts[i] = (counts[i] ?? 0) + 1;
  }
  const w = kindIndexFromTileId(winningTile);
  counts[w] = (counts[w] ?? 0) + 1;
  return counts;
}

/**
 * 判定是否成胡并计算番型。纯计算，不触碰牌局状态。
 *
 * - 缺门校验：`voidSuit` 非空且手牌仍含该花色牌 → `rejectReason: 'void_suit_held'`；
 * - 否则牌型无法拆解 → `rejectReason: 'no_valid_decomposition'`；
 * - 封顶：M1 锁定 `M1_ENFORCED_FAN_CAP`（D-01，`fanCap = 4`）。
 */
export function checkWin(input: WinCheckInput): WinCheckResult {
  const meldCount = input.melds.length;
  const counts = buildCounts(input.hand, input.winningTile);

  if (!canWinCounts(counts, meldCount, input.voidSuit)) {
    const reason =
      input.voidSuit !== null && hasQueTileCounts(counts, input.voidSuit)
        ? 'void_suit_held'
        : 'no_valid_decomposition';
    return { isWin: false, fanTags: [], rawFan: 0, cappedFan: 0, rejectReason: reason };
  }

  const ctx = deriveContext(input.winKind, input.isLastTile);
  const { fanTags, rawFan } = computeFan(counts, input.melds, input.rules.config, ctx, meldCount);
  const cappedFan = Math.min(rawFan, M1_ENFORCED_FAN_CAP);
  return { isWin: true, fanTags, rawFan, cappedFan, rejectReason: null };
}
